import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Calendar as CalendarIcon,
  Clock,
  Users,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== CLASS TYPES MANAGEMENT ==============

export const ClassTypesManager = () => {
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [error, setError] = useState('');

  const fetchClassTypes = async () => {
    try {
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/class-types?active_only=false`, {
        withCredentials: true
      });
      setClassTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch class types:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassTypes();
  }, []);

  const handleEdit = (classType) => {
    setEditingType(classType);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this class type?')) return;
    
    try {
      
      await axios.delete(`${BACKEND_URL}/api/admin/class-types/${id}`, {
        withCredentials: true
      });
      fetchClassTypes();
    } catch (err) {
      setError('Failed to delete class type');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingType(null);
    fetchClassTypes();
  };

  if (loading) {
    return <div className="loading-state">Loading class types...</div>;
  }

  return (
    <div className="class-types-manager">
      <div className="manager-header">
        <div>
          <h2>Class Types</h2>
          <p>Create templates for your classes</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="add-btn">
          <Plus size={18} />
          Add Class Type
        </Button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <ClassTypeForm 
          classType={editingType} 
          onClose={handleFormClose}
        />
      )}

      <div className="class-types-grid">
        {classTypes.length === 0 ? (
          <Card className="empty-state">
            <CardContent>
              <CalendarIcon size={48} />
              <h3>No Class Types Yet</h3>
              <p>Create your first class type to start scheduling classes.</p>
              <Button onClick={() => setShowForm(true)}>
                <Plus size={18} />
                Create Class Type
              </Button>
            </CardContent>
          </Card>
        ) : (
          classTypes.map((classType) => (
            <ClassTypeCard 
              key={classType.id}
              classType={classType}
              onEdit={() => handleEdit(classType)}
              onDelete={() => handleDelete(classType.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

const ClassTypeCard = ({ classType, onEdit, onDelete }) => {
  const categoryColors = {
    bungee: '#FF6B35',
    aerial: '#3B82F6',
    coaching: '#22C55E'
  };

  return (
    <Card className={`class-type-card ${!classType.is_active ? 'inactive' : ''}`}>
      <div 
        className="card-color-bar" 
        style={{ backgroundColor: classType.color || categoryColors[classType.category] }}
      />
      <CardContent className="card-body">
        <div className="card-header-row">
          <span className={`category-badge ${classType.category}`}>
            {classType.category}
          </span>
          {!classType.is_active && <span className="inactive-badge">Inactive</span>}
        </div>
        
        <h3 className="class-name">{classType.name}</h3>
        <p className="class-description">{classType.short_description}</p>
        
        <div className="class-details">
          <div className="detail-item">
            <Clock size={16} />
            <span>{classType.duration_minutes} min</span>
          </div>
          <div className="detail-item">
            <Users size={16} />
            <span>Max {classType.max_capacity}</span>
          </div>
          <div className="detail-item">
            <DollarSign size={16} />
            <span>${classType.price}</span>
          </div>
        </div>
        
        <div className="card-actions">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit2 size={14} />
            Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="delete-btn">
            <Trash2 size={14} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ClassTypeForm = ({ classType, onClose }) => {
  const [formData, setFormData] = useState({
    name: classType?.name || '',
    category: classType?.category || 'bungee',
    short_description: classType?.short_description || '',
    full_description: classType?.full_description || '',
    duration_minutes: classType?.duration_minutes || 60,
    price: classType?.price || 35,
    max_capacity: classType?.max_capacity || 5,
    min_capacity: classType?.min_capacity || 1,
    default_instructor: classType?.default_instructor || 'Cal',
    color: classType?.color || '#FF6B35',
    is_public: classType?.is_public ?? true,
    requires_waiver: classType?.requires_waiver ?? true
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        
        const response = await axios.get(`${BACKEND_URL}/api/admin/categories`, {
          withCredentials: true
        });
        setCategories(response.data);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        // Fallback to defaults
        setCategories([
          { id: 'bungee', name: 'Bungee Fitness' },
          { id: 'aerial', name: 'Aerial Kids' },
          { id: 'coaching', name: 'Personal Operations' }
        ]);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      
      

      if (classType?.id) {
        // Update
        await axios.put(
          `${BACKEND_URL}/api/admin/class-types/${classType.id}`,
          formData,
          { withCredentials: true }
        );
      } else {
        // Create
        await axios.post(
          `${BACKEND_URL}/api/admin/class-types`,
          formData,
          { withCredentials: true }
        );
      }
      
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save class type');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-overlay">
      <div className="form-modal">
        <div className="form-header">
          <h3>{classType ? 'Edit Class Type' : 'Create Class Type'}</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="class-type-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="name">Class Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Bungee 101 Fitness"
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="category">Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="form-group">
            <Label htmlFor="short_description">Short Description *</Label>
            <Input
              id="short_description"
              name="short_description"
              value={formData.short_description}
              onChange={handleChange}
              placeholder="Brief description for listings"
              required
            />
          </div>

          <div className="form-group">
            <Label htmlFor="full_description">Full Description</Label>
            <textarea
              id="full_description"
              name="full_description"
              value={formData.full_description}
              onChange={handleChange}
              placeholder="Detailed description for class page"
              rows={4}
              className="textarea-input"
            />
          </div>

          <div className="form-row triple">
            <div className="form-group">
              <Label htmlFor="duration_minutes">Duration (min) *</Label>
              <Input
                id="duration_minutes"
                name="duration_minutes"
                type="number"
                value={formData.duration_minutes}
                onChange={handleChange}
                min={15}
                max={180}
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="price">Price ($) *</Label>
              <Input
                id="price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                min={0}
                step={0.01}
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="max_capacity">Max Capacity *</Label>
              <Input
                id="max_capacity"
                name="max_capacity"
                type="number"
                value={formData.max_capacity}
                onChange={handleChange}
                min={1}
                max={50}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="default_instructor">Default Instructor</Label>
              <Input
                id="default_instructor"
                name="default_instructor"
                value={formData.default_instructor}
                onChange={handleChange}
                placeholder="Cal"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="color">Calendar Color</Label>
              <div className="color-input-row">
                <input
                  type="color"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className="color-picker"
                />
                <Input
                  value={formData.color}
                  onChange={handleChange}
                  name="color"
                  className="color-text"
                />
              </div>
            </div>
          </div>

          <div className="form-checkboxes">
            <div className="checkbox-item">
              <Checkbox
                id="is_public"
                checked={formData.is_public}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, is_public: checked }))
                }
              />
              <Label htmlFor="is_public">Show on public schedule</Label>
            </div>
            <div className="checkbox-item">
              <Checkbox
                id="requires_waiver"
                checked={formData.requires_waiver}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, requires_waiver: checked }))
                }
              />
              <Label htmlFor="requires_waiver">Requires signed waiver</Label>
            </div>
          </div>

          <div className="form-actions">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (classType ? 'Update Class Type' : 'Create Class Type')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ============== CLASS SCHEDULER ==============

export const ClassScheduler = () => {
  const [classTypes, setClassTypes] = useState([]);
  const [instances, setInstances] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // week or month
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      
      

      // Fetch class types
      const typesRes = await axios.get(`${BACKEND_URL}/api/admin/class-types`, { withCredentials: true });
      setClassTypes(typesRes.data);

      // Fetch instances for current view
      const startDate = getWeekStart(currentDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (view === 'week' ? 7 : 35));

      const instancesRes = await axios.get(
        `${BACKEND_URL}/api/admin/calendar?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`,
        { withCredentials: true }
      );
      setInstances(instancesRes.data);
    } catch (err) {
      console.error('Failed to fetch schedule data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowScheduleForm(true);
  };

  const handleInstanceClick = (instance) => {
    setSelectedInstance(instance);
  };

  const handleFormClose = (success = false) => {
    setShowScheduleForm(false);
    setSelectedDate(null);
    setSelectedInstance(null);
    if (success) {
      setMessage('Class scheduled successfully!');
      setTimeout(() => setMessage(''), 3000);
    }
    fetchData();
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const jumpToDate = (dateStr) => {
    if (dateStr) {
      setCurrentDate(new Date(dateStr));
    }
  };

  // Generate week days
  const weekStart = getWeekStart(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);
    return date;
  });

  const hours = Array.from({ length: 14 }, (_, i) => i + 6); // 6 AM to 7 PM

  const getInstancesForSlot = (date, hour) => {
    return instances.filter(instance => {
      const instanceDate = new Date(instance.start);
      return (
        instanceDate.toDateString() === date.toDateString() &&
        instanceDate.getHours() === hour
      );
    });
  };

  if (loading && instances.length === 0) {
    return <div className="loading-state">Loading schedule...</div>;
  }

  return (
    <div className="class-scheduler">
      {message && <div className="success-message">{message}</div>}
      
      <div className="scheduler-header">
        <div className="scheduler-nav">
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
            <ChevronLeft size={18} />
          </Button>
          <h3>
            {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - 
            {' '}{weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </h3>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
            <ChevronRight size={18} />
          </Button>
          <Input
            type="date"
            className="date-jump"
            onChange={(e) => jumpToDate(e.target.value)}
            title="Jump to date"
          />
        </div>
        <Button onClick={() => setShowScheduleForm(true)} className="schedule-btn">
          <Plus size={18} />
          Schedule Class
        </Button>
      </div>

      {classTypes.length === 0 && (
        <div className="no-class-types-warning">
          <AlertCircle size={18} />
          <span>Create a class type first before scheduling classes.</span>
        </div>
      )}

      {showScheduleForm && (
        <ScheduleClassForm
          classTypes={classTypes}
          selectedDate={selectedDate}
          onClose={handleFormClose}
        />
      )}

      {selectedInstance && (
        <InstanceDetailModal
          instance={selectedInstance}
          onClose={() => setSelectedInstance(null)}
          onUpdate={fetchData}
        />
      )}

      <div className="calendar-grid">
        <div className="calendar-header">
          <div className="time-column-header"></div>
          {weekDays.map((day, i) => (
            <div 
              key={i} 
              className={`day-header ${day.toDateString() === new Date().toDateString() ? 'today' : ''}`}
            >
              <span className="day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
              <span className="day-number">{day.getDate()}</span>
            </div>
          ))}
        </div>

        <div className="calendar-body">
          {hours.map((hour) => (
            <div key={hour} className="calendar-row">
              <div className="time-label">
                {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
              </div>
              {weekDays.map((day, dayIndex) => {
                const slotInstances = getInstancesForSlot(day, hour);
                return (
                  <div 
                    key={dayIndex} 
                    className="calendar-cell"
                    onClick={() => handleDateClick(new Date(day.setHours(hour, 0, 0, 0)))}
                  >
                    {slotInstances.map((instance) => (
                      <div
                        key={instance.id}
                        className={`calendar-event ${instance.extendedProps?.status}`}
                        style={{ backgroundColor: instance.color }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInstanceClick(instance);
                        }}
                      >
                        <span className="event-title">{instance.title}</span>
                        <span className="event-bookings">{instance.extendedProps?.bookings}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ScheduleClassForm = ({ classTypes, selectedDate, onClose }) => {
  const [formData, setFormData] = useState({
    class_type_id: '',
    start_date: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
    start_time: selectedDate ? `${String(selectedDate.getHours()).padStart(2, '0')}:00` : '09:00',
    instructor: '',
    max_capacity: '',
    is_private: false,
    private_group_tag: ''
  });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkData, setBulkData] = useState({
    end_date: '',
    days_of_week: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate
    if (!formData.class_type_id) {
      setError('Please select a class type');
      setLoading(false);
      return;
    }
    
    if (!formData.start_date) {
      setError('Please select a date');
      setLoading(false);
      return;
    }

    try {
      
      

      if (bulkMode) {
        if (!bulkData.end_date) {
          setError('Please select an end date for recurring classes');
          setLoading(false);
          return;
        }
        if (bulkData.days_of_week.length === 0) {
          setError('Please select at least one day of the week');
          setLoading(false);
          return;
        }
        
        // Bulk create
        await axios.post(
          `${BACKEND_URL}/api/admin/class-instances/bulk`,
          null,
          {
            withCredentials: true,
            params: {
              class_type_id: formData.class_type_id,
              start_date: formData.start_date,
              end_date: bulkData.end_date,
              days_of_week: bulkData.days_of_week,
              time_of_day: formData.start_time
            }
          }
        );
      } else {
        // Single create
        const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
        
        await axios.post(
          `${BACKEND_URL}/api/admin/class-instances`,
          {
            class_type_id: formData.class_type_id,
            start_time: startDateTime.toISOString(),
            instructor: formData.instructor || null,
            max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null,
            is_private: formData.is_private,
            private_group_tag: formData.private_group_tag || null
          },
          { withCredentials: true }
        );
      }

      onClose(true); // Pass true to indicate success
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to schedule class. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const daysOfWeek = [
    { value: 0, label: 'Mon' },
    { value: 1, label: 'Tue' },
    { value: 2, label: 'Wed' },
    { value: 3, label: 'Thu' },
    { value: 4, label: 'Fri' },
    { value: 5, label: 'Sat' },
    { value: 6, label: 'Sun' }
  ];

  const toggleDay = (day) => {
    setBulkData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  return (
    <div className="form-overlay">
      <div className="form-modal">
        <div className="form-header">
          <h3>Schedule Class</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="schedule-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <Label>Class Type *</Label>
            <Select 
              value={formData.class_type_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, class_type_id: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a class type" />
              </SelectTrigger>
              <SelectContent>
                {classTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name} ({type.category})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bulk-toggle">
            <Checkbox
              id="bulkMode"
              checked={bulkMode}
              onCheckedChange={setBulkMode}
            />
            <Label htmlFor="bulkMode">Schedule recurring classes</Label>
          </div>

          <div className="form-row">
            <div className="form-group">
              <Label>{bulkMode ? 'Start Date' : 'Date'} *</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <Label>Time *</Label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                required
              />
            </div>
          </div>

          {bulkMode && (
            <>
              <div className="form-group">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={bulkData.end_date}
                  onChange={(e) => setBulkData(prev => ({ ...prev, end_date: e.target.value }))}
                  required={bulkMode}
                />
              </div>

              <div className="form-group">
                <Label>Days of Week *</Label>
                <div className="days-selector">
                  {daysOfWeek.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      className={`day-btn ${bulkData.days_of_week.includes(day.value) ? 'selected' : ''}`}
                      onClick={() => toggleDay(day.value)}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="form-row">
            <div className="form-group">
              <Label>Instructor (optional)</Label>
              <Input
                value={formData.instructor}
                onChange={(e) => setFormData(prev => ({ ...prev, instructor: e.target.value }))}
                placeholder="Leave blank for default"
              />
            </div>
            <div className="form-group">
              <Label>Max Capacity (optional)</Label>
              <Input
                type="number"
                value={formData.max_capacity}
                onChange={(e) => setFormData(prev => ({ ...prev, max_capacity: e.target.value }))}
                placeholder="Leave blank for default"
              />
            </div>
          </div>

          <div className="checkbox-item">
            <Checkbox
              id="is_private"
              checked={formData.is_private}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_private: checked }))}
            />
            <Label htmlFor="is_private">Private class (not shown on public schedule)</Label>
          </div>

          {formData.is_private && (
            <div className="form-group">
              <Label>Group Name</Label>
              <Input
                value={formData.private_group_tag}
                onChange={(e) => setFormData(prev => ({ ...prev, private_group_tag: e.target.value }))}
                placeholder="e.g., Nicole Baddies"
              />
            </div>
          )}

          <div className="form-actions">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Scheduling...' : (bulkMode ? 'Schedule Classes' : 'Schedule Class')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InstanceDetailModal = ({ instance, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this class?')) return;
    
    setLoading(true);
    try {
      
      await axios.delete(`${BACKEND_URL}/api/admin/class-instances/${instance.id}`, {
        withCredentials: true
      });
      onUpdate();
      onClose();
    } catch (err) {
      alert('Failed to cancel class');
    } finally {
      setLoading(false);
    }
  };

  const startTime = new Date(instance.start);

  return (
    <div className="form-overlay">
      <div className="form-modal instance-detail">
        <div className="form-header">
          <h3>{instance.title}</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="instance-details">
          <div className="detail-row">
            <CalendarIcon size={18} />
            <span>{startTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
          <div className="detail-row">
            <Clock size={18} />
            <span>{startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
          </div>
          <div className="detail-row">
            <Users size={18} />
            <span>{instance.extendedProps?.bookings} booked</span>
          </div>
          {instance.extendedProps?.instructor && (
            <div className="detail-row">
              <span>Instructor: {instance.extendedProps.instructor}</span>
            </div>
          )}
          <div className="detail-row">
            <span className={`status-badge ${instance.extendedProps?.status}`}>
              {instance.extendedProps?.status}
            </span>
          </div>
        </div>

        <div className="form-actions">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {instance.extendedProps?.status === 'scheduled' && (
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={loading}
            >
              {loading ? 'Cancelling...' : 'Cancel Class'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default { ClassTypesManager, ClassScheduler };
